<template>
  <div>

    <b-card body-bg-variant="light"
            header="General information"
            header-bg-variant="dark"
            header-text-variant="light">

      <energy-range v-model="endStationParameters"
                    :beamline-specification="endStationSpecification"
                    :disabled="!editable"
                    :is_photon_energy_from_invalid="$v.endStationParameters.photon_energy_from"
                    :is_photon_energy_to_invalid="$v.endStationParameters.photon_energy_to"></energy-range>

      <label for="photonPolarisation">Photon polarisation</label>
      <extended-checkbox-group id="photonPolarisation"
                               v-model="endStationParameters.photon_polarisation"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.photon_polarisation"
                               :options="photonPolarisation"/>

      <label for="detectionModes">Detection modes</label>
      <extended-checkbox-group id="detectionModes"
                               v-model="endStationParameters.detection_modes"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.detection_modes"
                               :options="detectionModes"
                               :stacked="true"
                               class="detection_modes"/>

      <template v-if="isExpertCommissioningAccessModeAlertShown">
        <b-alert
          :show="true"
          class="alert"
          variant="danger">
          <h5 class="font-weight-bold">
            Remember that SR-ARPES is currently available ONLY in Expert Commissioning Access mode.
            For more information visit this
            <a class="sol-link"
               href="https://synchrotron.uj.edu.pl/en_GB/uzytkownicy/nabory-wnioskow"
               rel="noopener noreferrer"
               target="_blank">
              website</a>.
          </h5>
        </b-alert>
      </template>

      <temperature-range v-model="endStationParameters"
                         :beamline-specification="endStationSpecification"
                         :disabled="!editable"
                         :value_from_invalid="$v.endStationParameters.measurement_temperature_from"
                         :value_to_invalid="$v.endStationParameters.measurement_temperature_to">
      </temperature-range>

      <p class="p-form-label">Do you want to use cryogenic liquid?</p>
      <extended-checkbox-group v-model="endStationParameters.cryogenic_liquids"
                               :disabled="!editable"
                               :isInvalid="this.$v.endStationParameters.cryogenic_liquids"
                               :options="cryogenicLiquids"
                               unselectBtn="None"/>

      <template v-if="hasProperty(endStationParameters, 'cryogenic_liquids')
                  && endStationParameters.cryogenic_liquids.find(el => el === 'LHe')">
        <p class="p-form-label">Please clarify why do you need liquid helium</p>
        <p class="p-form-description">(max 300 characters incl. spaces)</p>
        <b-form-textarea v-model="endStationParameters.lhe_use_justification"
                         :class="{'custom-invalid-input-border custom-input-border-radius': $v.endStationParameters.lhe_use_justification.$error}"
                         placeholder="Enter missing value"
                         @input="$v.endStationParameters.lhe_use_justification.$touch()"/>
      </template>
    </b-card>

    <b-card body-bg-variant="light"
            header="Sample preparation and environment"
            header-bg-variant="dark"
            header-text-variant="light">

      <preparation-in-situ v-model="endStationParameters"
                           :beamline-specification="endStationSpecification"
                           :disabled="!editable"
                           :is-preparation-in-situ-invalid="$v.endStationParameters.preparation_in_situ"
                           :is-preparation-temperature-from-invalid="$v.endStationParameters.preparation_temperature_from"
                           :is-preparation-temperature-to-invalid="$v.endStationParameters.preparation_temperature_to"/>

      <label for="evaporation">Evaporation</label>
      <extended-radio-group id="evaporation"
                            v-model="endStationParameters.evaporation"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.evaporation"
                            :options="yesNo"></extended-radio-group>
      <label v-if="endStationParameters.evaporation"
             class="grey--text"
             for="evaporationData">
        Contact beamline scientist about actually used materials.</label>
      <two-data-list v-if="endStationParameters.evaporation"
                     id="evaporationData"
                     v-model="endStationParameters.evaporation_data"
                     :disabled="!editable"
                     :multiselect=true
                     :options="evaporationOptions"></two-data-list>

      <label for="arSputtering">Ar-sputtering</label>
      <extended-radio-group id="arSputtering"
                            v-model="endStationParameters.ar_sputtering"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.ar_sputtering"
                            :options="yesNo"></extended-radio-group>

      <label for="crystalClever">Crystal clever</label>
      <extended-radio-group id="crystalClever"
                            v-model="endStationParameters.crystal_clever"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.crystal_clever"
                            :options="yesNo"></extended-radio-group>

      <label for="reactionChamber">Reaction chamber</label>
      <extended-radio-group id="reactionChamber"
                            v-model="endStationParameters.reaction_chamber"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.reaction_chamber"
                            :options="yesNo"></extended-radio-group>

      <template v-if="endStationParameters.reaction_chamber">
        <p class="grey--text font-weight-bold"
           v-html="reactionChamberDataSpecification"/>
        <label v-if="reactionChamberRangeValidatorError"
               class="custom-text-danger"
               for="reactionChamberData">
          Check if all values are not out of range.
        </label>
        <three-data-list id="reactionChamberData"
                         v-model="endStationParameters.reaction_chamber_data"
                         :disabled="!editable"
                         :options="reactionChamberOptions"/>
      </template>
    </b-card>
  </div>
</template>

<script>
  import { endStationValidator } from '@/assets/validators/endStationAFormValidator';
  import ExtendedRadioGroup from '@/components/ExtendedRadioGroup';
  import beamlineSpecification from '@/json/beamlineSpecification';
  import energyRange from '@/components/proposal/proposals/energy-range';
  import temperatureRange from '@/components/proposal/proposals/temperature-range';
  import preparationInSitu from '@/components/proposal/proposals/PreparationInSitu';
  import twoDataList from '@/components/two_data_list';
  import threeDataList from '@/components/three_data_list';
  import ExtendedCheckboxGroup from '@/components/ExtendedCheckboxGroup';
  import yesNo from '@/json/radioButtonYesNo';
  import {
    evaporationOptions,
    reactionChamberOptions,
    photonPolarisation,
    cryogenicLiquids,
    detectionModes,
  } from '@/helpers/proposal-form/endStationA';

  export default {
    name: 'EndStationAForm',
    components: {
      ExtendedRadioGroup,
      energyRange,
      temperatureRange,
      preparationInSitu,
      twoDataList,
      ExtendedCheckboxGroup,
      threeDataList,
    },
    props: ['value', 'editable'],
    data() {
      return {
        endStationSpecification: beamlineSpecification.endStationASpecification,
        endStationParameters: this.value,
        yesNo,
        evaporationOptions,
        reactionChamberOptions,
        photonPolarisation,
        cryogenicLiquids,
        detectionModes,
      };
    },
    computed: {
      isExpertCommissioningAccessModeAlertShown() {
        return this.endStationParameters.detection_modes?.length && this.endStationParameters.detection_modes.includes(
          'In Expert Commissioning Access: Spin Resolved Angle Resolved Photoelectron Spectroscopy (SR-ARPES)',
        );
      },
      reactionChamberDataSpecification() {
        const consultGasUsage = 'Consult gas usage with beamline manager!';

        const minTemperature = this.endStationSpecification.reaction_chamber_temperature.from;
        const maxTemperature = this.endStationSpecification.reaction_chamber_temperature.to;
        const temperatureRangeText = `Reaction temperature range: ${minTemperature} - ${maxTemperature} K`;

        const minPressure = this.endStationSpecification.reaction_chamber_gas_pressure.from;
        const maxPressure = this.endStationSpecification.reaction_chamber_gas_pressure.to;
        const pressureRangeText = `Gas pressure range: ${minPressure} - ${maxPressure} mbar`;

        return `${consultGasUsage} </br></br> ${pressureRangeText} </br> ${temperatureRangeText}`;
      },
      reactionChamberRangeValidatorError() {
        const allFieldsFilled = this.$v.endStationParameters.reaction_chamber_data.notEmptyField;
        const rangeValidationError = !this.$v.endStationParameters.reaction_chamber_data.rangeValidation;
        return allFieldsFilled && rangeValidationError;
      },
    },
    watch: {
      endStationParameters() {
        this.$emit('input', this.endStationParameters);
        this.$emit('beamline-validation', this.$v.endStationParameters);
      },
      'endStationParameters.cryogenic_liquids': {
        handler() {
          if (!this.isLHeChecked()) {
            delete this.endStationParameters.lhe_use_justification;
          }
        },
      },
      'endStationParameters.evaporation': {
        handler() {
          if (!this.endStationParameters.evaporation && this.endStationParameters.evaporation_data) {
            delete this.endStationParameters.evaporation_data;
          }
        },
      },
      'endStationParameters.reaction_chamber': {
        handler() {
          if (!this.endStationParameters.reaction_chamber && this.endStationParameters.reaction_chamber_data) {
            delete this.endStationParameters.reaction_chamber_data;
          }
        },
      },
    },
    created() {
      this.$emit('beamline-validation', this.$v.endStationParameters);
    },
    methods: {
      isLHeChecked() {
        return this.endStationParameters.cryogenic_liquids
          && this.endStationParameters.cryogenic_liquids.includes('LHe');
      },
    },
    validations: endStationValidator,
  };
</script>

<style scoped>
  .detection_modes {
    margin-bottom: 25px;
  }

  .flex-div {
    display: flex;
    flex-flow: column nowrap;
  }
</style>
